import type { FC } from 'react';
import {
    Box,
    Container,
    Typography,
    Card,
    Stack,
    useTheme,
    Modal,
    IconButton,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import PublicIcon from '@mui/icons-material/Public';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import CollectionsIcon from '@mui/icons-material/Collections';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CloseIcon from '@mui/icons-material/Close';
import { useState } from 'react';

const solutions = [
    {
        icon: <CurrencyExchangeIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
        title: 'Exchanges',
    },
    {
        icon: <PublicIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
        title: 'Cross Border Payments',
    },
    {
        icon: <AccountBalanceWalletIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
        title: 'Wallets',
    },
    {
        icon: <SportsEsportsIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
        title: 'Gaming',
    },
    {
        icon: <CollectionsIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
        title: 'NFTs',
    },
];

const ONRAMP_VIDEO_URL = 'https://framerusercontent.com/assets/e1s3ZWQsLCFUXQ0dupcYcpBlCgw.mp4';
const OFFRAMP_VIDEO_URL = 'https://framerusercontent.com/assets/Eeiee2Tu2NpsxRvdRgwUUBVkIpo.mp4';

export const HomeHowItHelpsHowItWorks: FC = () => {
    const theme = useTheme();
    const [openVideo, setOpenVideo] = useState(false);
    const [currentVideo, setCurrentVideo] = useState('');

    const handleOpenVideo = (videoUrl: string) => {
        setCurrentVideo(videoUrl);
        setOpenVideo(true);
    };
    const handleCloseVideo = () => setOpenVideo(false);

    return (
        <>
            <Box
                sx={{
                    py: 15,
                    background: 'linear-gradient(180deg, #F8FAFC 0%, #EFF6FF 100%)',
                    width: '100vw',
                    position: 'relative',
                    marginLeft: 'calc(-50vw + 50%)',
                    marginRight: 'calc(-50vw + 50%)',
                }}
            >
                <Container maxWidth="lg">
                    <Typography
                        variant="h4"
                        sx={{
                            fontWeight: 600,
                            textAlign: 'center',
                            mb: 8,
                            color: 'text.primary',
                            fontSize: { xs: '2rem', md: '2.5rem' }
                        }}
                    >
                        SwiftRamp helps businesses solve problems
                    </Typography>

                    <Stack
                        direction="row"
                        spacing={4}
                        sx={{
                            flexWrap: 'wrap',
                            gap: 4,
                            justifyContent: 'center'
                        }}
                    >
                        {solutions.map((solution, index) => (
                            <Card
                                key={index}
                                sx={{
                                    width: 220,
                                    height: 220,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: 'white',
                                    borderRadius: 4,
                                    boxShadow: theme.shadows[2],
                                    // transition: 'transform 0.3s, box-shadow 0.3s',
                                    // '&:hover': {
                                    //     transform: 'translateY(-8px)',
                                    //     boxShadow: theme.shadows[8],
                                    // },
                                    p: 3,
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 80,
                                        height: 80,
                                        borderRadius: 3,
                                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mb: 2,
                                    }}
                                >
                                    {solution.icon}
                                </Box>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        textAlign: 'center',
                                        fontWeight: 600,
                                        color: 'text.primary',
                                    }}
                                >
                                    {solution.title}
                                </Typography>
                            </Card>
                        ))}
                    </Stack>
                </Container>
            </Box>

            <Box
                sx={{
                    py: 15,
                    backgroundColor: '#0B0F18',
                    width: '100vw',
                    position: 'relative',
                    marginLeft: 'calc(-50vw + 50%)',
                    marginRight: 'calc(-50vw + 50%)',
                }}
            >
                <Container maxWidth="lg">
                    <Typography
                        variant="h2"
                        sx={{
                            fontWeight: 700,
                            textAlign: 'center',
                            mb: 8,
                            color: 'common.white',
                            fontSize: { xs: '2rem', md: '2.5rem' }
                        }}
                    >
                        See how it works
                    </Typography>

                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={4}
                        alignItems="center"
                        justifyContent="center"
                    >
                        <Box sx={{ textAlign: 'center' }}>
                            <Card
                                onClick={() => handleOpenVideo(ONRAMP_VIDEO_URL)}
                                sx={{
                                    width: { xs: '100%', md: 500 },
                                    height: 280,
                                    backgroundColor: 'background.paper',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    mb: 2,
                                    position: 'relative',
                                    cursor: 'pointer',
                                    '&:hover .play-icon': {
                                        transform: 'scale(1.1)',
                                        backgroundColor: '#4F46E5',
                                    }
                                }}
                            >
                                <Box
                                    className="play-icon"
                                    sx={{
                                        width: 64,
                                        height: 64,
                                        borderRadius: '50%',
                                        backgroundColor: '#6366F1',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        transition: 'transform 0.2s',
                                    }}
                                >
                                    <PlayArrowIcon sx={{ fontSize: 40, color: 'common.white' }} />
                                </Box>
                            </Card>
                            <Typography
                                variant="h6"
                                sx={{
                                    color: 'common.white',
                                    fontWeight: 600,
                                    mb: 1
                                }}
                            >
                                Onramp
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: alpha('#fff', 0.7),
                                }}
                            >
                                Instant Fiat Deposit
                            </Typography>
                        </Box>

                        <Box sx={{ textAlign: 'center' }}>
                            <Card
                                onClick={() => handleOpenVideo(OFFRAMP_VIDEO_URL)}
                                sx={{
                                    width: { xs: '100%', md: 500 },
                                    height: 280,
                                    backgroundColor: 'background.paper',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    mb: 2,
                                    position: 'relative',
                                    cursor: 'pointer',
                                    '&:hover .play-icon': {
                                        transform: 'scale(1.1)',
                                        backgroundColor: '#4F46E5',
                                    }
                                }}
                            >
                                <Box
                                    className="play-icon"
                                    sx={{
                                        width: 64,
                                        height: 64,
                                        borderRadius: '50%',
                                        backgroundColor: '#6366F1',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        transition: 'transform 0.2s',
                                    }}
                                >
                                    <PlayArrowIcon sx={{ fontSize: 40, color: 'common.white' }} />
                                </Box>
                            </Card>
                            <Typography
                                variant="h6"
                                sx={{
                                    color: 'common.white',
                                    fontWeight: 600,
                                    mb: 1
                                }}
                            >
                                Offramp
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: alpha('#fff', 0.7),
                                }}
                            >
                                Seamless INR Withdrawals
                            </Typography>
                        </Box>
                    </Stack>
                </Container>
            </Box>

            {/* Video Modal */}
            <Modal
                open={openVideo}
                onClose={handleCloseVideo}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Box
                    sx={{
                        position: 'relative',
                        width: { xs: '100%', sm: '90%', md: '80%' },
                        maxWidth: '1000px',
                        bgcolor: 'background.paper',
                        borderRadius: 2,
                        p: 1,
                    }}
                >
                    <IconButton
                        onClick={handleCloseVideo}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: 'white',
                            bgcolor: 'rgba(0,0,0,0.5)',
                            '&:hover': {
                                bgcolor: 'rgba(0,0,0,0.7)',
                            },
                            zIndex: 1,
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                    <Box
                        component="video"
                        autoPlay
                        controls
                        sx={{
                            width: '100%',
                            height: 'auto',
                            maxHeight: '80vh',
                            borderRadius: 1,
                            display: 'block',
                        }}
                    >
                        <source src={currentVideo}
type="video/mp4" />
                        Your browser does not support the video tag.
                    </Box>
                </Box>
            </Modal>
        </>
    );
};
